import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Tag } from 'primeng/tag';
import { Textarea } from 'primeng/textarea';

import { AuthService } from '../../core/services/auth.service';
import { ContactoService } from '../../core/services/contacto.service';
import { UserStore } from '../../core/services/user-store.service';

interface InicioBloque {
  icon: string;
  titulo: string;
  texto: string;
}

interface InicioMetrica {
  etiqueta: string;
  objetivo: number;
  valor: ReturnType<typeof signal<number>>;
  /** Clase PrimeIcons (ej. `pi pi-users`). */
  icono: string;
}

@Component({
  selector: 'app-inicio',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, Button, Card, Tag, InputText, Select, Textarea],
  templateUrl: './inicio.component.html',
  styleUrl: './inicio.component.scss',
})
export class InicioComponent implements AfterViewInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private observador?: IntersectionObserver;
  private animacion?: number;
  private parallaxRaf?: number;
  private readonly userStore = inject(UserStore);
  private readonly fb = inject(FormBuilder);
  private readonly contacto = inject(ContactoService);
  private readonly messages = inject(MessageService);
  private readonly reduceMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  @ViewChild('quienesFoto') private quienesFoto?: ElementRef<HTMLImageElement>;

  readonly metricas: InicioMetrica[] = [
    { etiqueta: 'Cant. líderes', objetivo: 27, valor: signal(0), icono: 'pi pi-sitemap' },
    { etiqueta: 'Pers. en célula', objetivo: 280, valor: signal(0), icono: 'pi pi-users' },
    { etiqueta: 'Pers. sin célula', objetivo: 46, valor: signal(0), icono: 'pi pi-user' },
  ];

  readonly conSesion = this.auth.isAuthenticated();
  readonly rutaEntrada = this.conSesion ? this.userStore.rutaInicial() : '/login';
  readonly enviando = signal(false);
  readonly enviado = signal(false);
  readonly scrolled = signal(false);
  readonly animacionesActivas = signal(!this.reduceMotion);

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.scrolled.set(window.scrollY > 12);
    this.programarParallaxQuienes();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.contactoForm.dirty || this.enviado() || this.enviando()) {
      return;
    }
    event.preventDefault();
    event.returnValue = true;
  }

  readonly paises = [
    'Argentina',
    'Bolivia',
    'Chile',
    'Colombia',
    'Costa Rica',
    'Cuba',
    'Ecuador',
    'El Salvador',
    'España',
    'Estados Unidos',
    'Guatemala',
    'Honduras',
    'México',
    'Nicaragua',
    'Panamá',
    'Paraguay',
    'Perú',
    'Puerto Rico',
    'República Dominicana',
    'Uruguay',
    'Venezuela',
    'Otro',
  ];

  readonly provinciasArgentina = [
    'Buenos Aires',
    'Ciudad Autónoma de Buenos Aires',
    'Catamarca',
    'Chaco',
    'Chubut',
    'Córdoba',
    'Corrientes',
    'Entre Ríos',
    'Formosa',
    'Jujuy',
    'La Pampa',
    'La Rioja',
    'Mendoza',
    'Misiones',
    'Neuquén',
    'Río Negro',
    'Salta',
    'San Juan',
    'San Luis',
    'Santa Cruz',
    'Santa Fe',
    'Santiago del Estero',
    'Tierra del Fuego',
    'Tucumán',
  ];

  readonly horarios = [
    'Mañana, de 9 a 12',
    'Tarde, de 12 a 18',
    'Noche, de 18 a 21',
    'Una hora en específico',
  ];
  readonly notaMax = 500;

  readonly contactoForm = this.fb.nonNullable.group({
    nombre: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(80)]],
    iglesia: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(120)]],
    celular: ['', [Validators.required, Validators.pattern(/^[0-9+\s()-]{8,20}$/)]],
    pais: ['Argentina', Validators.required],
    paisOtro: [{ value: '', disabled: true }],
    provincia: ['', Validators.required],
    horario: ['', Validators.required],
    hora: [{ value: '', disabled: true }],
    nota: ['', Validators.maxLength(500)],
  });

  readonly pasos: InicioBloque[] = [
    {
      icon: 'pi pi-users',
      titulo: 'El administrador prepara la información',
      texto:
        'Trabaja en apoyo del Pastor y mantiene ordenada la información de la congregación, los líderes, las células y las personas que necesitan acompañamiento.',
    },
    {
      icon: 'pi pi-check-square',
      titulo: 'El líder acompaña',
      texto:
        'Registra la asistencia de su célula y mantiene actualizado el seguimiento de cada persona, sin cargar con información que no le corresponde.',
    },
    {
      icon: 'pi pi-chart-line',
      titulo: 'El Pastor entiende lo que pasa',
      texto:
        'Los datos se convierten en resúmenes y gráficos simples para que el Pastor pueda ver la realidad de su iglesia y actuar a tiempo.',
    },
  ];

  readonly funciones: InicioBloque[] = [
    {
      icon: 'pi pi-users',
      titulo: 'Personas bajo liderazgo',
      texto:
        'El Pastor sabe con exactitud cuántas personas están bajo el cuidado de cada líder y cuáles todavía esperan ser integradas a una célula.',
    },
    {
      icon: 'pi pi-exclamation-circle',
      titulo: 'Personas que se están por apartar',
      texto:
        'Detectá a quienes faltaron dos domingos seguidos y recibí una señal temprana para acompañarlos antes de que se desconecten.',
    },
    {
      icon: 'pi pi-map',
      titulo: 'Mapa de células',
      texto:
        'Visualizá en un mapa dónde están las células de la iglesia y obtené una mirada clara de cómo se distribuye tu comunidad.',
    },
    {
      icon: 'pi pi-chart-bar',
      titulo: 'Gráficos y resúmenes',
      texto:
        'El Pastor recibe indicadores fáciles de leer: asistencia, edades, bautismos, sexo y evolución de las células.',
    },
    {
      icon: 'pi pi-calendar',
      titulo: 'Reuniones y asistencia',
      texto:
        'Registrá las reuniones de la iglesia y compará la asistencia semana a semana para reconocer tendencias.',
    },
    {
      icon: 'pi pi-comments',
      titulo: 'Seguimiento sin perder el hilo',
      texto:
        'El administrador y el líder registran información relevante sobre cada persona para que el Pastor pueda seguir el acompañamiento desde un solo lugar.',
    },
  ];

  readonly roles: InicioBloque[] = [
    {
      icon: 'pi pi-building',
      titulo: 'Administrador',
      texto:
        'Trabaja en apoyo del Pastor: administra personas y células, ordena la información y prepara una visión clara de lo que sucede en la iglesia.',
    },
    {
      icon: 'pi pi-sitemap',
      titulo: 'Líder',
      texto:
        'Se enfoca en su propia célula: registra la asistencia, acompaña a sus personas y comunica lo que necesita atención para que el Pastor tenga una visión completa.',
    },
  ];

  ngAfterViewInit(): void {
    const root = this.host.nativeElement as HTMLElement;
    const piezas = Array.from(root.querySelectorAll('.inicio-revelar')) as HTMLElement[];
    if (this.reduceMotion || !('IntersectionObserver' in window)) {
      this.animacionesActivas.set(false);
      piezas.forEach((pieza) => pieza.classList.add('is-visible'));
      this.completarMetricas();
      return;
    }

    this.observador = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((entrada) => {
          if (!entrada.isIntersecting) {
            return;
          }
          const pieza = entrada.target as HTMLElement;
          if (pieza.classList.contains('inicio-graficos-visual')) {
            if (entrada.intersectionRatio >= 0.3) {
              this.animarMetricas();
              this.observador?.unobserve(pieza);
            }
            return;
          }
          pieza.classList.add('is-visible');
          this.observador?.unobserve(pieza);
        });
      },
      { threshold: [0.3, 0.7] },
    );
    piezas.forEach((pieza) => this.observador?.observe(pieza));
    const visual = root.querySelector('.inicio-graficos-visual') as HTMLElement | null;
    if (visual) {
      this.observador.observe(visual);
    }
    this.programarParallaxQuienes();
  }

  ngOnDestroy(): void {
    this.observador?.disconnect();
    if (this.animacion) {
      cancelAnimationFrame(this.animacion);
    }
    if (this.parallaxRaf) {
      cancelAnimationFrame(this.parallaxRaf);
    }
  }

  private programarParallaxQuienes(): void {
    if (this.reduceMotion || this.parallaxRaf) {
      return;
    }
    this.parallaxRaf = requestAnimationFrame(() => {
      this.parallaxRaf = undefined;
      this.actualizarParallaxQuienes();
    });
  }

  private actualizarParallaxQuienes(): void {
    const foto = this.quienesFoto?.nativeElement;
    const seccion = this.host.nativeElement.querySelector('#quienes') as HTMLElement | null;
    if (!foto || !seccion) {
      return;
    }
    const rect = seccion.getBoundingClientRect();
    const vista = window.innerHeight || 1;
    const progreso = (vista - rect.top) / (vista + rect.height);
    const offset = Math.max(-14, Math.min(14, (progreso - 0.5) * 28));
    foto.style.transform = `translate3d(0, ${offset}px, 0) scale(1.04)`;
  }

  private animarMetricas(): void {
    const inicio = performance.now();
    const duracion = 1200;
    const tick = (ahora: number) => {
      const avance = Math.min(1, (ahora - inicio) / duracion);
      const curva = 1 - (1 - avance) ** 3;
      this.zone.run(() => {
        this.metricas.forEach((metrica) => metrica.valor.set(Math.round(metrica.objetivo * curva)));
      });
      if (avance < 1) {
        this.animacion = requestAnimationFrame(tick);
      }
    };
    this.animacion = requestAnimationFrame(tick);
  }

  private completarMetricas(): void {
    this.metricas.forEach((metrica) => metrica.valor.set(metrica.objetivo));
  }

  private enfocarPrimerError(): void {
    const orden = [
      'nombre',
      'iglesia',
      'celular',
      'pais',
      'paisOtro',
      'provincia',
      'horario',
      'hora',
      'nota',
    ] as const;
    for (const control of orden) {
      const campo = this.contactoForm.controls[control];
      if (campo.disabled || campo.valid) {
        continue;
      }
      const el = this.host.nativeElement.querySelector(
        `#contacto-${control === 'paisOtro' ? 'pais-otro' : control}`,
      ) as HTMLElement | null;
      el?.focus();
      return;
    }
  }

  irA(id: string): void {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  esArgentina(): boolean {
    return this.contactoForm.controls.pais.value === 'Argentina';
  }

  esOtroPais(): boolean {
    return this.contactoForm.controls.pais.value === 'Otro';
  }

  esHoraEspecifica(): boolean {
    return this.contactoForm.controls.horario.value === 'Una hora en específico';
  }

  alCambiarPais(): void {
    const otro = this.contactoForm.controls.paisOtro;
    this.contactoForm.controls.provincia.setValue('');
    if (this.esOtroPais()) {
      otro.enable();
      otro.setValidators([Validators.required, Validators.minLength(2)]);
    } else {
      otro.setValue('');
      otro.clearValidators();
      otro.disable();
    }
    otro.updateValueAndValidity();
  }

  alCambiarHorario(): void {
    const hora = this.contactoForm.controls.hora;
    if (this.esHoraEspecifica()) {
      hora.enable();
      hora.setValidators(Validators.required);
    } else {
      hora.setValue('');
      hora.clearValidators();
      hora.disable();
    }
    hora.updateValueAndValidity();
  }

  enviarContacto(): void {
    if (this.contactoForm.invalid) {
      this.contactoForm.markAllAsTouched();
      queueMicrotask(() => this.enfocarPrimerError());
      return;
    }

    const raw = this.contactoForm.getRawValue();
    this.enviando.set(true);
    this.contacto
      .enviar({
        nombre: raw.nombre.trim(),
        iglesia: raw.iglesia.trim(),
        celular: raw.celular.trim(),
        pais: this.esOtroPais() ? raw.paisOtro.trim() : raw.pais,
        provincia: raw.provincia.trim(),
        horario: this.esHoraEspecifica() ? `A las ${raw.hora}` : raw.horario,
        nota: raw.nota.trim(),
      })
      .subscribe({
        next: () => {
          this.enviando.set(false);
          this.enviado.set(true);
          this.contactoForm.reset({
            pais: 'Argentina',
            paisOtro: '',
            nombre: '',
            iglesia: '',
            celular: '',
            provincia: '',
            horario: '',
            hora: '',
            nota: '',
          });
          this.contactoForm.controls.paisOtro.disable();
          this.contactoForm.controls.hora.disable();
          this.messages.add({
            severity: 'success',
            summary: 'Pedido enviado',
            detail: 'Te vamos a contactar en el horario que elegiste.',
            life: 5000,
          });
        },
        error: (err: HttpErrorResponse) => {
          this.enviando.set(false);
          this.messages.add({
            severity: 'error',
            summary: 'No pudimos enviar el pedido',
            detail: err.status === 0 ? 'No se pudo contactar al servidor.' : 'Intentá de nuevo en unos minutos.',
            life: 5000,
          });
        },
      });
  }
}
